import { Button, Typography } from '@material-ui/core'
import { useMemo } from 'react'

import { isolate } from '../../../../helpers/bidi'
import { formatPromotionMinorAmount } from '../../../../helpers/promotionAmount'
import { getTranslate, lang } from '../../../../localization'
import { AppliedPromotion, CartItem } from '../../../../types/cart'
import useStyles from './styles/index.styles'

type AppliedOfferProps = {
  applied: AppliedPromotion
  code: string
  /** Cart lines supply public room/rate-plan names for evaluator line references. */
  cartItems?: CartItem[]
  disabled: boolean
  onRemove: () => void
}

/**
 * What the booker actually got, in money.
 *
 * This is the one place in the section that can state a real amount. `discountMinor` comes
 * back from `evaluatePromotion` computed against THIS cart by the promotion service — it is
 * not derived here from a percentage and a subtotal. That distinction is the whole reason
 * the offer rows above show a rate ("25%") and this row shows a figure: reconstructing the
 * second from the first is precisely the class of bug that made the cart's pre-discount
 * total wrong for months, and a client is the worst possible place to repeat it.
 *
 * It replaces the code field rather than sitting under it. An editable input beside the
 * word "applied" invites an edit that silently invalidates the verdict next to it.
 */
const AppliedOffer = ({
  applied,
  code,
  cartItems = [],
  disabled,
  onRemove
}: AppliedOfferProps) => {
  const classes = useStyles()
  const noCodeDiscount = /^0+$/.test(applied.discountMinor)
  const allocations = applied.allocations ?? []
  const cartItemsByID = useMemo(
    () => new Map(cartItems.map((item) => [item.id, item])),
    [cartItems]
  )
  const roomLabel = (roomStayReference: string, allocationIndex: number) => {
    const item = cartItemsByID.get(roomStayReference)

    // Before a primitive reservation, `roomStayReference` need not yet be a cart-line
    // identity. `appliedLineIds` is the server's paired legacy cart-line projection.
    // Use it only when it has a one-to-one correspondence with allocations: guessing by
    // position from an incomplete response would misattribute money to a room.
    const pairedLineIDs = applied.appliedLineIds
    const lineID =
      pairedLineIDs && allocations.length === pairedLineIDs.length
        ? pairedLineIDs[allocationIndex]
        : undefined
    const allocatedItem =
      item ?? (lineID ? cartItemsByID.get(lineID) : undefined)

    // Neither identifier is booker-facing content; join the authoritative identifier to
    // the selected cart line before presenting the allocation.
    return allocatedItem
      ? `${allocatedItem.roomType.name} (${allocatedItem.ratePlan.displayRatePlanCode})`
      : getTranslate('Selected room')
  }

  return (
    <div className={classes.appliedRow} role="status">
      <div className={classes.appliedHeader}>
        <div className={classes.appliedText}>
          <Typography
            variant="body1"
            className={
              noCodeDiscount ? classes.noCodeDiscount : classes.appliedAmount
            }>
            {getTranslate(
              noCodeDiscount
                ? 'Code {code} was accepted, but did not reduce the price.'
                : 'Discount with code {code} applied.',
              { code: lang === 'fa' ? isolate(code) : code }
            )}
          </Typography>
        </div>
        <Button
          color="primary"
          disabled={disabled}
          onClick={onRemove}
          size="small"
          variant="text">
          {getTranslate('Remove')}
        </Button>
      </div>
      <div className={classes.codeDiscountRow}>
        <Typography variant="body2">{getTranslate('Code discount')}</Typography>
        <Typography
          variant="body2"
          dir="ltr"
          className={
            noCodeDiscount ? classes.noCodeDiscount : classes.appliedAmount
          }>
          {`${noCodeDiscount ? '' : '− '}${formatPromotionMinorAmount(
            applied.discountMinor,
            applied.currency
          )} ${getTranslate(applied.currency)}`}
        </Typography>
      </div>
      {allocations.length > 0 && (
        <div className={classes.allocationList}>
          {allocations.map((allocation, index) => (
            <div
              className={classes.allocationRow}
              key={`${allocation.roomStayReference}-${index}`}>
              <Typography variant="caption" color="textSecondary">
                {roomLabel(allocation.roomStayReference, index)}
              </Typography>
              <Typography variant="caption" color="textSecondary" dir="ltr">
                {`− ${formatPromotionMinorAmount(
                  allocation.discountMinor,
                  allocation.currency || applied.currency
                )} ${getTranslate(allocation.currency || applied.currency)}`}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AppliedOffer
